import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import * as fromRoot from '@app/reducers';
import { addOrUpdateUrlParam } from '@app/shared/helpers/urlHelper';
import { Observable, of, ReplaySubject } from 'rxjs';
import { mergeMap, take, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { RedirectionService } from '../services/redirection.service';
import { AccountHasDomainGuard } from './base/account-has-domain.guard';
import { AdminGuard } from './base/admin.guard';
import { IsAccountPremiumGuard } from './base/is-account-premium.guard';
import { IsPremiumTokenValidGuard } from './base/is-premium-token-valid.guard';
import { IsSubscriptionValidGuard } from './base/is-subscription-valid.guard';
import { LoadRolesAndTeamsGuard } from './base/load-roles-and-teams.guard';
import { Store } from '@ngrx/store';
import { LoadDomainGuard } from './base/load-domain.guard';
import { LoadServicesGuard } from './base/load-services.guard';
import { isPlatformServer } from '@angular/common';
import { getProtocolHostnameAndPort, isAppDomain } from '@app/shared/helpers/location';
import { environment } from 'src/environments/environment';
import { encodeSamlRequest } from 'smash-saml';
@Injectable({
  providedIn: 'root'
})
export class AccountPremiumCanActivateGuard  {

  constructor(
    private store$: Store<fromRoot.State>,
    private userHasTeam: AccountHasDomainGuard,
    private loadDomainGuard: LoadDomainGuard,
    private loadServicesGuard: LoadServicesGuard,
    private isLoggedInGuard: IsAccountPremiumGuard,
    private isTokenValidGuard: IsPremiumTokenValidGuard,
    private loadRolesAndTeamsGuard: LoadRolesAndTeamsGuard,
    private adminGuard: AdminGuard,
    private isSubscriptionValidGuard: IsSubscriptionValidGuard,
    private redirectionService: RedirectionService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) {
      return of(true)
    }
    return this.loadDomainGuard.canActivate().pipe(mergeMap((domainLoaded: boolean) => {
      if (domainLoaded) {
        return this.loadServicesGuard.canActivate().pipe(mergeMap((servicesLoaded: boolean) => {
          if (servicesLoaded) {
            return this.isLoggedInGuard.canActivate().pipe(mergeMap((isLoggedIn: boolean) => {
              if (isLoggedIn) {
                return this.isTokenValidGuard.canActivate().pipe(mergeMap((isTokenValid: boolean) => {
                  if (isTokenValid) {
                    return this.loadRolesAndTeamsGuard.canActivate().pipe(mergeMap((rolesAndTeamsLoaded: boolean) => {
                      if (rolesAndTeamsLoaded) {
                        return this.adminGuard.canActivate().pipe(mergeMap((isAdmin: boolean) => {
                          if (isAdmin) {
                            return this.isSubscriptionValidGuard.canActivate().pipe(mergeMap((isSubscriptionValid: boolean) => {
                              if (isSubscriptionValid) {
                                return this.userHasTeam.canActivate().pipe(mergeMap((userHasTeam: boolean) => {
                                  if (userHasTeam) {
                                    return of(true);
                                  } else {
                                    this.redirectionService.redirectToSignupDomain();
                                  }
                                }));
                              } else {
                                return of(false);
                              }
                            }));
                          } else {
                            return this.userHasTeam.canActivate().pipe(mergeMap((userHasTeam: boolean) => {
                              if (userHasTeam) {
                                return of(true);
                              } else {
                                this.redirectionService.redirectToInvalidConfiguration();
                              }
                            }));
                          }
                        }));
                      }
                    }));
                  }
                }));
              } else {
                return this.chechRedirectionFromDomainOptions();
              }
            }));
          }
        }));
      }
    }));
  }

  chechRedirectionFromDomainOptions(): Observable<boolean> {
    const stayHere$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      withLatestFrom(
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.selectServices),
        this.store$.select(fromRoot.getCurrentDomainProviders),
      ),
      take(1),
    ).subscribe(([state, currentDomain, { services }, providers]) => {
      if (currentDomain?.options?.redirection?.value) {
        const redirectTo = currentDomain.options.redirection.value;
        switch (redirectTo) {
          case Constant.domainLoginRedirection.None:
            window.location.href = '/signin';
            break;
          case Constant.domainLoginRedirection.Signin:
            window.location.href = '/signin';
            break;
          case Constant.domainLoginRedirection.Provider:
            if (providers?.length > 0) {
              const idpUrl = providers[0].idpUrl;
              const audienceUris = providers[0].audienceUris;
              const consumerServiceUrl = services.find(service => service.name === 'iam' && service.region === currentDomain.region).url + '/saml/signon';
              const correspondingAudienceUri = isAppDomain() ? environment.appDomainWithProtocol : audienceUris.find(audienceUri => audienceUri === getProtocolHostnameAndPort());
              const SAMLRequest = encodeSamlRequest({ consumerServiceUrl, audienceUri: correspondingAudienceUri });
              window.location.href = addOrUpdateUrlParam(idpUrl, Constant.SAMLRequestKey, SAMLRequest);
              stayHere$.next(false);
              break;
            } else {
              stayHere$.next(true);
            }
          default:
            stayHere$.next(true);
        }
      } else {
        stayHere$.next(true);
      }
    });
    return stayHere$.asObservable();
  }

}



